import { useEffect, useRef, useState, useCallback } from 'react';
import { type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';

import { tokenManager } from '@/lib/api';
import { createAppSocket } from '@/lib/socket';

interface TypingState {
  userId: string;
  conversationId: string;
}

export function useChatSocket(activeConversationId: string | undefined) {
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const [typingUsers, setTypingUsers] = useState<TypingState[]>([]);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const token = tokenManager.getAccessToken();
    if (!token) return;

    const socket = createAppSocket(token);

    socketRef.current = socket;

    socket.on('chat:message', (message: { conversationId: string }) => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
      queryClient.invalidateQueries({
        queryKey: ['chat-messages', message.conversationId],
      });
      // A reply from the counterpart resets the unanswered-message counter.
      queryClient.invalidateQueries({ queryKey: ['chat-daily-count'] });
    });

    socket.on('chat:unread', () => {
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    });

    socket.on('chat:status-update', (data: { messageIds: string[]; status: string; conversationId?: string }) => {
      // Update message status in cache
      queryClient.setQueriesData<{ data: Array<{ id: string; status: string }> }>(
        { queryKey: ['chat-messages'] },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((msg) =>
              data.messageIds.includes(msg.id) ? { ...msg, status: data.status } : msg
            ),
          };
        }
      );
      // Also invalidate to ensure fresh data from API as fallback
      if (data.conversationId) {
        queryClient.invalidateQueries({ queryKey: ['chat-messages', data.conversationId] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['chat-messages'] });
      }
    });

    socket.on('typing:start', (data: TypingState) => {
      setTypingUsers((prev) => {
        if (prev.some((t) => t.userId === data.userId && t.conversationId === data.conversationId)) {
          return prev;
        }
        return [...prev, data];
      });
    });

    socket.on('typing:stop', (data: TypingState) => {
      setTypingUsers((prev) =>
        prev.filter(
          (t) => !(t.userId === data.userId && t.conversationId === data.conversationId)
        )
      );
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [queryClient]);

  // Join/leave conversation room when active conversation changes
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !activeConversationId) return;

    socket.emit('join:conversation', activeConversationId);

    return () => {
      socket.emit('leave:conversation', activeConversationId);
    };
  }, [activeConversationId]);

  const emitTypingStart = useCallback(
    (conversationId: string) => {
      const socket = socketRef.current;
      if (!socket) return;

      socket.emit('typing:start', conversationId);

      // Auto-stop after 3 seconds
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        socket.emit('typing:stop', conversationId);
      }, 3000);
    },
    []
  );

  const emitTypingStop = useCallback((conversationId: string) => {
    const socket = socketRef.current;
    if (!socket) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    socket.emit('typing:stop', conversationId);
  }, []);

  const isTyping = useCallback(
    (conversationId: string) =>
      typingUsers.some((t) => t.conversationId === conversationId),
    [typingUsers]
  );

  return {
    isTyping,
    emitTypingStart,
    emitTypingStop,
  };
}
